'use client'

import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalDescription,
  ResponsiveModalTitle,
  ResponsiveModalTrigger,
} from '@/components/extension/responsive-modal'
import {
  Carousel,
  CarouselContent,
  CarouselDots,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { cn } from '@/lib/utils'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { useState } from 'react'

export interface Ambassador {
  readonly name: string
  readonly profession: string
  readonly birthday?: string
  readonly description1?: string
  readonly description2?: string
  readonly achievement?: readonly string[]
  readonly quote?: string
  readonly images: readonly string[]
}

interface AmbassadorItem {
  readonly tag?: string
  readonly name?: string
  readonly role?: string
  readonly image: string
  readonly modalData?: Ambassador
}

export interface AmbassadorMiddleData {
  readonly type: 'ambassadors' | 'communities' | 'organizations'
  readonly title: string
  readonly items: readonly AmbassadorItem[]
}

const AmbassadorQuote = ({ quote }: { quote: string }) => (
  <div className='mt-2 rounded bg-gray-50 p-3 text-sm italic'>
    <svg
      width='41'
      height='34'
      viewBox='0 0 41 34'
      fill='currentColor'
      xmlns='http://www.w3.org/2000/svg'
      className='mb-3 text-primary opacity-50'
    >
      <path
        opacity='1'
        d='M40.8 15.7898V34H23.1897V18.4983C23.1897 8.89378 27.9749 2.72768 37.5453 0L40.7419 5.87797C37.9134 6.83842 35.8405 7.99096 34.5231 9.33559C33.2444 10.6802 32.6051 12.8316 32.6051 15.7898H40.8ZM17.6684 15.7898V34H0V18.4983C0 8.89378 4.80456 2.72768 14.4137 0L17.6103 5.87797C15.7117 6.53107 14.2393 7.16497 13.1932 7.77966C10.6746 9.27797 9.41538 11.948 9.41538 15.7898H17.6684Z'
        fill='currentColor'
      />
    </svg>
    <p>&quot;{quote}&quot;</p>
  </div>
)

const AmbassadorModal = ({
  ambassador,
  open,
  onOpenChange,
}: {
  ambassador: Ambassador
  open: boolean
  onOpenChange: (open: boolean) => void
}) => {
  const t = useTranslations()

  return (
    <ResponsiveModal open={open} onOpenChange={onOpenChange}>
      <ResponsiveModalTrigger />
      <ResponsiveModalContent
        className='gap-0 border-none px-5 py-6 lg:max-w-[800px]'
        aria-describedby={'modal-login'}
      >
        <ResponsiveModalTitle />
        <ResponsiveModalDescription />
        <div className='no-scrollbar max-h-[80vh] space-y-6 overflow-y-auto'>
          <h3 className='mb-4 text-2xl font-medium leading-none text-neutral-900 md:text-4xl'>
            {t(ambassador.name)}
          </h3>

          <div className='mb-6 flex items-center gap-3'>
            <div className='rounded-full bg-primary px-4 text-lg leading-[32px] text-white'>
              {t(ambassador.profession)}
            </div>
            {ambassador.birthday && (
              <div className='rounded-full border border-neutral-500 px-4 font-criteria text-xl leading-[32px] text-neutral-500'>
                {ambassador.birthday}
              </div>
            )}
          </div>

          {ambassador.birthday && (
            <div className='mb-2 text-sm text-gray-600'>
              {t(
                'landing_dai_su_the_thao_page.section_sports_ambassador_birthday'
              )}
              : {ambassador.birthday}
            </div>
          )}

          {(ambassador.description1 || ambassador.description2) && (
            <div className='mb-2'>
              <div className='mb-1 font-bold'>
                {t(
                  'landing_dai_su_the_thao_page.section_sports_ambassador_introduction'
                )}
              </div>
              {ambassador.description1 && (
                <div>{t(ambassador.description1)}</div>
              )}
              {ambassador.description2 && (
                <div>{t(ambassador.description2)}</div>
              )}
            </div>
          )}

          {ambassador.achievement && ambassador.achievement.length > 0 && (
            <div className='mb-2'>
              <div className='mb-1 font-bold'>
                {ambassador.birthday
                  ? t(
                      'landing_dai_su_the_thao_page.section_sports_ambassador_achievement'
                    )
                  : t('landing_dai_su_the_thao_page.achievements')}
              </div>
              <ul className='list-disc pl-5 text-left'>
                {ambassador.achievement.map((item: string, i: number) => (
                  <li key={i}>{t(item)}</li>
                ))}
              </ul>
            </div>
          )}

          {ambassador.quote && <AmbassadorQuote quote={t(ambassador.quote)} />}

          {ambassador?.images && ambassador.images.length > 1 ? (
            <Carousel className='w-full' opts={{ loop: true }}>
              <CarouselContent>
                {ambassador.images?.map((img: string, idx: number) => (
                  <CarouselItem key={idx}>
                    <Image
                      src={ImageHelper.getImageUrl(img)}
                      alt={ambassador.name}
                      width={700}
                      height={400}
                      className='w-auto rounded-lg object-cover'
                    />
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselDots />
            </Carousel>
          ) : null}
        </div>
      </ResponsiveModalContent>
    </ResponsiveModal>
  )
}

interface AmbassadorMiddleProps {
  data: AmbassadorMiddleData
}

export const AmbassadorMiddle = ({ data }: AmbassadorMiddleProps) => {
  const t = useTranslations()
  const [selectedAmbassador, setSelectedAmbassador] =
    useState<Ambassador | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  if (!data?.items?.length) return null

  // If it's organizations, use Carousel with 2 rows, 2 items per row on mobile, 5 on desktop
  if (data.type === 'organizations') {
    const groupedItems: AmbassadorItem[][] = []
    for (let i = 0; i < data.items.length; i += 2) {
      groupedItems.push(data.items.slice(i, i + 2))
    }

    return (
      <div className='mb-8 md:mb-20'>
        <Carousel
          opts={{
            align: 'start',
            loop: true,
          }}
          className='w-full'
        >
          <div className='mb-4 flex items-center justify-between md:mb-8'>
            <h3 className='font-criteria text-2xl font-medium uppercase text-[#111111] md:text-[28px]'>
              {data.title}
            </h3>
            <div className='hidden gap-3 md:flex'>
              <CarouselPrevious className='static h-10 w-10 translate-x-0 translate-y-0 rounded-full border-none bg-gray-200 text-black hover:bg-gray-300' />
              <CarouselNext className='static h-10 w-10 translate-x-0 translate-y-0 rounded-full border-none bg-[#111] text-white hover:bg-black/80' />
            </div>
          </div>
          <CarouselContent className='-ml-4'>
            {groupedItems.map((group, idx) => (
              <CarouselItem key={idx} className='basis-1/2 pl-4 lg:basis-1/5'>
                <div className='flex flex-col gap-4'>
                  {group.map((item, itemIdx) => (
                    <div
                      key={itemIdx}
                      className='group cursor-pointer overflow-hidden rounded-[20px]'
                    >
                      <Image
                        width={700}
                        height={400}
                        src={ImageHelper.getN7ImageUrl(
                          '/image/2026/03/23/Card-profile.png'
                        )}
                        alt='Partner Logo'
                        className='h-auto w-full object-cover transition-transform duration-500 group-hover:scale-105'
                      />
                    </div>
                  ))}
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
      </div>
    )
  }

  // Common Carousel wrapper for ambassadors and communities
  return (
    <div className='mb-8 md:mb-20'>
      <Carousel
        opts={{
          align: 'start',
          loop: true,
        }}
        className='w-full'
      >
        <div className='mb-4 flex items-center justify-between md:mb-8'>
          <h3 className='font-criteria text-2xl font-medium uppercase text-[#111111] md:text-[28px]'>
            {data.title}
          </h3>
          <div className='hidden gap-3 md:flex'>
            <CarouselPrevious className='static h-10 w-10 translate-x-0 translate-y-0 rounded-full border-none bg-gray-200 text-black hover:bg-gray-300' />
            <CarouselNext className='static h-10 w-10 translate-x-0 translate-y-0 rounded-full border-none bg-[#111] text-white hover:bg-black/80' />
          </div>
        </div>

        <CarouselContent className='-ml-4'>
          {data.items.map((item, idx: number) => {
            return (
              <CarouselItem
                key={idx}
                className={cn(
                  'basis-[71.428%] pl-4 md:basis-[31.25%] lg:basis-[21.739%]',
                  !item.modalData && 'cursor-default'
                )}
              >
                <div
                  className={`group relative aspect-[386/493] w-full overflow-hidden rounded-2xl bg-gray-200`}
                  onClick={() => {
                    if (item.modalData) {
                      setSelectedAmbassador(item.modalData)
                      setIsModalOpen(true)
                    }
                  }}
                >
                  <Image
                    width={700}
                    height={400}
                    src={ImageHelper.getN7ImageUrl(item.image)}
                    alt={item.name || 'Image'}
                    className='absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105'
                  />
                  <div className='absolute inset-x-0 bottom-0 bg-gradient-to-t from-black via-black/60 to-transparent p-5 pt-16 md:p-6 md:pt-20'>
                    <h4 className='font-criteria text-base font-bold text-white md:text-xl'>
                      {item.name ? t(item.name) : ''}
                    </h4>
                    {item.role && (
                      <p className='mt-1 text-xs text-white/80 md:text-sm'>
                        {t(item.role)}
                      </p>
                    )}
                  </div>
                  {item.tag && (
                    <div className='absolute left-4 top-4 rounded-full bg-[#D9FF00] px-3 py-1 text-xs font-bold text-black'>
                      {item.tag}
                    </div>
                  )}
                </div>
              </CarouselItem>
            )
          })}
        </CarouselContent>
      </Carousel>

      {isModalOpen && selectedAmbassador && (
        <AmbassadorModal
          ambassador={selectedAmbassador}
          open={isModalOpen}
          onOpenChange={setIsModalOpen}
        />
      )}
    </div>
  )
}
